import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface InstrumentDto {
  id: number;
  symbol: string;
  description: string;
  digits: number;
  storedGmtOffsetSec: number;
  enabled: boolean;
  bars?: number;
}

interface SymbolContextValue {
  /** The selected pair, or undefined until instruments have loaded. */
  instrument: InstrumentDto | undefined;
  symbol: string | undefined;
  instruments: InstrumentDto[];
  /** Only the pairs marked visible in settings. */
  available: InstrumentDto[];
  setSymbol: (symbol: string) => void;
  isLoading: boolean;
}

const SymbolContext = createContext<SymbolContextValue | null>(null);

const STORAGE_KEY = 'pipyar-symbol';

/**
 * The selected trading pair, shared by every page.
 *
 * A context rather than a URL search param: the choice persists across
 * navigation and reloads, which is what you want from a topbar selector — a
 * param would reset each time you moved between modules, and putting it in
 * every route's search schema would couple all of them to it.
 */
export function SymbolProvider({ children }: { children: ReactNode }) {
  const query = useQuery({
    queryKey: ['settings', 'instruments'],
    queryFn: () => api.get<{ instruments: InstrumentDto[] }>('/api/settings/instruments'),
    staleTime: 60_000,
  });

  const instruments = useMemo(() => query.data?.instruments ?? [], [query.data]);
  const available = useMemo(() => instruments.filter((i) => i.enabled), [instruments]);

  const [selected, setSelected] = useState<string | undefined>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? undefined;
    } catch {
      return undefined;
    }
  });

  // Fall back to the first visible pair when nothing is stored, or when the
  // stored one has been hidden or removed in settings.
  const instrument = useMemo(() => {
    const match = available.find((i) => i.symbol === selected);
    return match ?? available[0];
  }, [available, selected]);

  useEffect(() => {
    if (!instrument) return;
    try {
      localStorage.setItem(STORAGE_KEY, instrument.symbol);
    } catch {
      /* private mode — the choice just will not persist */
    }
  }, [instrument]);

  const value = useMemo<SymbolContextValue>(
    () => ({
      instrument,
      symbol: instrument?.symbol,
      instruments,
      available,
      setSymbol: setSelected,
      isLoading: query.isPending,
    }),
    [instrument, instruments, available, query.isPending],
  );

  return <SymbolContext.Provider value={value}>{children}</SymbolContext.Provider>;
}

export function useSymbol(): SymbolContextValue {
  const ctx = useContext(SymbolContext);
  if (!ctx) throw new Error('useSymbol must be used inside a SymbolProvider');
  return ctx;
}
